import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types/swiper-options'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import { swiperNavigationOptions, swiperPaginationOptions } from 'Shared/SwiperControls/SwiperControls'

type SwiperOptionsExtended = SwiperOptions & {
    navigationPagination?: boolean
}

const createNavigationElement = (carousel: HTMLElement) => {
    const buttonPrev: HTMLButtonElement = document.createElement('button')
    buttonPrev.classList.add('swiper-navigation__button')
    buttonPrev.classList.add('swiper-navigation__button-prev')
    buttonPrev.ariaLabel = `{{js.swiper.navigation.slide.previous.aria-label}}`

    const buttonNext: HTMLButtonElement = document.createElement('button')
    buttonNext.classList.add('swiper-navigation__button')
    buttonNext.classList.add('swiper-navigation__button-next')
    buttonNext.ariaLabel = `{{js.swiper.navigation.slide.next.aria-label}}`

    return { buttonPrev, buttonNext }
}

const createNavigation = (carousel: HTMLElement) => {
    const navigation: HTMLElement = document.createElement('nav')
    navigation.classList.add('swiper-navigation')
    carousel.appendChild(navigation)

    const { buttonPrev, buttonNext } = createNavigationElement(carousel)
    navigation.appendChild(buttonPrev)
    navigation.appendChild(buttonNext)

    carousel.appendChild(navigation)
}

const createPaginationElement = (carousel: HTMLElement): HTMLElement => {
    const pagination: HTMLElement = document.createElement('nav')
    pagination.classList.add('swiper-pagination')
    return pagination
}

const createPagination = (carousel: HTMLElement) => {
    const pagination = createPaginationElement(carousel)
    carousel.appendChild(pagination)
}

const createControls = (carousel: HTMLElement) => {
    const container: HTMLElement = document.createElement('nav')
    container.classList.add('swiper-controls')
    carousel.appendChild(container)

    const pagination = createPaginationElement(carousel)
    const { buttonPrev, buttonNext } = createNavigationElement(carousel)

    container.appendChild(buttonPrev)
    container.appendChild(pagination)
    container.appendChild(buttonNext)
}

const createWrapper = (carousel: HTMLElement) => {
    const swiperWrapper = carousel.querySelector<HTMLDivElement>('.swiper-wrapper')
    if (!swiperWrapper) return
    const wrapper = document.createElement('div')
    wrapper.classList.add('swiper-wrapper-container')
    wrapper.appendChild(swiperWrapper)
    carousel.appendChild(wrapper)
}

const createCarousel = (query: string, swiperOptions: SwiperOptionsExtended) => {
    const carousels = document.querySelectorAll<HTMLElement>(query)
    carousels.forEach((carousel) => {
        carousel.classList.add('swiper')
        
        const children = [...carousel.children]
        children.forEach((child: HTMLElement) => {
            child.classList.add('swiper-slide')
        })

        if (swiperOptions.navigation !== false && swiperOptions.navigationPagination !== true) {
            createNavigation(carousel)
            swiperOptions.navigation =  swiperNavigationOptions
        }

        if (swiperOptions.pagination !== false && swiperOptions.navigationPagination !== true) {
            createPagination(carousel)
            swiperOptions.pagination = swiperPaginationOptions
        }

        if (swiperOptions.navigationPagination === true) {
            createControls(carousel)
            swiperOptions.navigation = swiperNavigationOptions
            swiperOptions.pagination = swiperPaginationOptions
        }

        const swiper = new Swiper(carousel, {
            modules: [ Navigation, Pagination, Autoplay ],
            createElements: true,
            watchOverflow: true,
            watchSlidesProgress: true,
            ...swiperOptions,
        })
        
        createWrapper(carousel)
    })
}

const createJobsCarousel = () => {
    createCarousel('.carousel-jobs .attrax-list-widget__list--has-items', {
        navigation: false,
        pagination: false,
        navigationPagination: true,
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 16,
        breakpoints: {
            666: {
                slidesPerView: 2,
                slidesPerGroup: 2,
            },
            991: {
                slidesPerView: 3,
                slidesPerGroup: 3,
            },
            1200: {
                slidesPerView: 4,
                slidesPerGroup: 4,
            },
        },
    })
}

const createPostsCarousel = () => {
    createCarousel('.carousel-posts .attrax-list-widget__list--has-items', {
        navigation: false,
        pagination: false,
        navigationPagination: true,
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 16,
        breakpoints: {
            666: {
                slidesPerView: 2,
                slidesPerGroup: 2,
            },
            991: {
                slidesPerView: 3,
                slidesPerGroup: 3,
            },
        },
    })
}

const createScrollingCtaCarousel = () => {
    createCarousel('.banner-cta', {
        navigation: false,
        pagination: false,
        loop: true,
        grabCursor: true,
        autoplay: {
            delay: 5000,
            pauseOnMouseEnter: true,
        },
    })
}

export const initialise = () => {
    createJobsCarousel()
    createPostsCarousel()
    createScrollingCtaCarousel()
}